import { Avatar, Box, Button, Card, CardContent, Grid, TextField, Typography } from "@mui/material";
import { IconButton, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { AppState } from "src/store/Store";
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
} from 'material-react-table';

import tg from "src/assets/images/socials/tg.png";
import vk from "src/assets/images/socials/vk.png";
import wa from "src/assets/images/socials/wa.png";
import { IconEye, IconMinus } from "@tabler/icons-react";
import { palette } from "src/theme/style/palette";
import { usePostBodyWithJwtMutation } from "src/redux/api/cabinet";
import { useDispatch } from "react-redux";
import { iLogout } from "src/redux/slices/app";
import { Alerting } from "src/views/components_cabinet/Alerting";
import { NumberFormat } from "src/components/hooks/number-format/NumberFormat";

interface UserData {
    id: number;
    login: string;
    first_name?: string;
    last_name?: string;
    avatar?: string;
    email: string;
    phone: string;
    telegram?: string;
    vk?: string;
    whatsapp?: string;
    created_at: string;
    balance: number;
    balance_null: number;
    balance_btc?: number;
    abonent_block?: number;
    abonent_block3?: number;
    abonent_block4?: number;
    abonent_block5?: number;
    referer_id?: number;
}

interface RefererData {
    name?: string;
    login?: string;
    phone?: string;
    email?: string;
    telegram?: string;
    created_at?: string;
}

interface PartnersData {
    info: UserData;
    sponsors: any;
    teams: UserData[];
    count: number;
    referer: RefererData;
}

interface IPartnersContent {
    dataArrProp: PartnersData;
    updPersonInfoId: (id: number) => void;
}

interface TableRowData {
    number: number;
    data: UserData;
}

interface AlertState {
    type: number;
    msg: string;
}

const socialMediaRegex: Record<string, RegExp> = {
    vk: /^https:\/\/vk\.com\/(id\d{1,15}|club\d{1,15}|public\d{1,15}|[a-zA-Z0-9_.]{3,32})$/,
    telegram: /^https:\/\/t\.me\/([a-zA-Z0-9_]{5,32}|\+?[a-zA-Z0-9_-]{10,50})$/,
    whatsapp: /^https:\/\/wa\.me\/\d{10,15}$/
};

const InfoBox: FC<{ value: string | number; subtitle: string }> = ({ value, subtitle }) => (
    <Box sx={{ background: palette.lightWhiteBg, borderRadius: 1, p: 1 }}>
        <Typography variant="h5">{value}</Typography>
        <Typography sx={{ color: "#9c9c9c" }}>{subtitle}</Typography>
    </Box>
);

export const PartnersContent: FC<IPartnersContent> = ({ dataArrProp, updPersonInfoId }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState<PartnersData>(dataArrProp);
    const [tableData, setTableData] = useState<TableRowData[]>([]);
    const [alert, setAlert] = useState<AlertState | undefined>(undefined);

    // Получаем начальное значение из URL или используем пустую строку
    const initialSearch = searchParams.get('search') || '';
    const [searchValue, setSearchValue] = useState(initialSearch);

    const dispatch = useDispatch();
    const { data: user } = useSelector((state: AppState) => state.app);
    const [getDataBuy, { isLoading }] = usePostBodyWithJwtMutation();

    const isValidSocialLink = useCallback((url: string, platform: string): boolean => {
        if (!url || typeof url !== 'string') return false;
        return socialMediaRegex[platform]?.test(url) || false;
    }, []);

    const renderSocialLink = useCallback((url: string, platform: string, icon: string) => {
        if (!url) return null;

        if (isValidSocialLink(url, platform)) {
            return (
                <a href={url} target="_blank" rel="noreferrer" style={{ marginRight: '8px' }}>
                    <Avatar src={icon} sx={{ width: 24, height: 24 }} />
                </a>
            );
        }

        return <Typography sx={{ mr: '8px' }}><IconMinus /></Typography>;
    }, [isValidSocialLink]);

    // Обновляем URL параметры при изменении userId
    const handleUpdPersonInfoId = useCallback((id: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('userId', id.toString());
        setSearchParams(params);
        updPersonInfoId(id);
    }, [searchParams, setSearchParams, updPersonInfoId]);

    const columns = useMemo<MRT_ColumnDef<TableRowData>[]>(
        () => [
            {
                accessorFn: (row) => (
                    <Typography variant="h6" sx={{ mr: 1.5 }}>
                        {row.number}
                    </Typography>
                ),
                header: '№',
                size: 10,
            },
            {
                accessorFn: (row) => (
                    <Box display='flex' alignItems='center'>
                        <Box
                            display='flex'
                            alignItems='center'
                            onClick={() => handleUpdPersonInfoId(row.data.id)}
                            sx={{ cursor: "pointer" }}
                        >
                            <IconEye style={{ width: '2rem', height: '2rem', color: palette.infoLight }} />
                            <Avatar
                                alt={`${row.data.first_name || ''} ${row.data.last_name || ''}`}
                                src={row.data.avatar || ''}
                            />
                        </Box>
                        <Box sx={{ ml: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {row.data.login}
                            </Typography>
                        </Box>
                    </Box>
                ),
                header: 'Логин',
                size: 20,
            },
            {
                accessorFn: (row) => (
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {dayjs(row.data.created_at).format('DD.MM.YYYY')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {dayjs(row.data.created_at).format('HH:mm:ss')}
                        </Typography>
                    </Box>
                ),
                header: 'Дата регистрации',
                size: 30,
            },
            {
                accessorFn: (row) => (
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '8px' }}>
                            {row.data.email}
                        </Typography>
                        <Box display='flex' justifyContent='start'>
                            {renderSocialLink(row.data.vk || '', 'vk', vk)}
                            {renderSocialLink(row.data.telegram || '', 'telegram', tg)}
                            {renderSocialLink(row.data.whatsapp || '', 'whatsapp', wa)}
                        </Box>
                    </Box>
                ),
                header: 'Контакты',
                size: 40,
            },
            {
                accessorFn: (row) => (
                    <Box>
                        <Typography sx={{ fontSize: "16px" }}>
                            Задолженность NULL: <b><NumberFormat number={row?.data?.abonent_block || 0} /></b>
                        </Typography>
                        <Typography sx={{ fontSize: "16px" }}>
                            Задолженность TAKE: <b><NumberFormat number={row?.data?.abonent_block3 || 0} /></b>
                        </Typography>
                        <Typography sx={{ fontSize: "16px" }}>
                            Задолженность BANK: <b><NumberFormat number={row?.data?.abonent_block4 || 0} /></b>
                        </Typography>
                        <Typography sx={{ fontSize: "16px" }}>
                            Задолженность ИНЬ: <b>{row?.data?.abonent_block5 || 0} BY</b>
                        </Typography>
                    </Box>
                ),
                header: 'Задолженность',
                size: 300,
            },
            {
                accessorFn: (row) => (
                    <Box display='flex' alignItems='center'>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            <NumberFormat symbol="BY" number={row.data.balance_btc || 0} />
                        </Typography>

                    </Box>
                ),
                header: 'Баланс BY',
                size: 15,
            },
            {
                accessorFn: (row) => (
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        <NumberFormat number={row.data.balance_null || 0} />
                    </Typography>
                ),
                header: 'Баланс NULL',
                size: 15,
            },

            {
                accessorFn: (row) => (
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        <NumberFormat number={row.data.balance || 0} />
                    </Typography>
                ),
                header: 'Баланс',
                size: 15,
            },

        ],
        [renderSocialLink, handleUpdPersonInfoId]
    );

    const table = useMaterialReactTable({
        columns,
        data: tableData,
        enableStickyHeader: false,
        enableStickyFooter: false,
        muiTablePaperProps: {
            elevation: 0,
            sx: { borderRadius: '0' },
        },
        enableGlobalFilter: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableColumnFilters: false,
        enableColumnActions: false,
        enableSorting: false,
        paginationDisplayMode: 'pages',
        muiTableBodyCellProps: {
            sx: {
                borderBottom: '1px solid #e5eaef',
                borderRight: '1px solid #e5eaef',
                '&:last-child': {
                    borderRight: 'none'
                }
            },
        },
        muiTableProps: {
            sx: { boxShadow: "none" }
        },
        muiTableContainerProps: {
            sx: { maxHeight: '100%', boxShadow: "none" }
        },
        muiTableHeadCellProps: {
            sx: {
                borderBottom: '1px solid #e5eaef',
                borderRight: '1px solid #e5eaef',
                fontSize: "1rem",
                '&:last-child': {
                    borderRight: 'none'
                }
            },
        },
        muiPaginationProps: {
            rowsPerPageOptions: [10, 30, 50, 100],
            showRowsPerPage: false,
            showFirstButton: false,
            showLastButton: false,
            shape: 'rounded',
            variant: 'outlined',
        },
        localization: {
            and: 'и',
            cancel: 'Отмена',
            search: 'Искать',
            showHideSearch: 'Поиск по всей таблице',
            rowsPerPage: 'Cтрок',
            goToLastPage: 'Предыдущая',
            goToNextPage: 'Следующая',
        },
        renderEmptyRowsFallback: () => (
            <Box py={5}>
                <Typography textAlign='center' variant="h3">Партнёров нет</Typography>
            </Box>
        ),
    });

    const initDataTable = useCallback((teams: UserData[]) => {
        const arrDataTable: TableRowData[] = teams.map((e, i) => ({
            number: i + 1,
            data: e
        }));
        setTableData(arrDataTable);
    }, []);

    const postRequest = useCallback(async (searchData: { search: string }) => {
        const jwt = localStorage.getItem('jwt');

        try {
            const payload = await getDataBuy({
                body: searchData,
                route: 'search-teams',
                jwt
            }).unwrap();

            setData(payload?.data);
        } catch (error: any) {
            if (error.data?.message === 'Unauthenticated.') {
                dispatch(iLogout());
            }
            setAlert({ type: 2, msg: error.data?.message || 'Ошибка поиска' });
        }
    }, [getDataBuy, dispatch]);

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchValue(value);

        // Обновляем URL параметр при изменении значения поиска
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('search', value);
        } else {
            params.delete('search');
        }
        setSearchParams(params);
    }, [searchParams, setSearchParams]);

    const handleSearchClick = useCallback(() => {
        if (searchValue.length >= 2) {
            postRequest({ search: searchValue });
        }
    }, [searchValue, postRequest]);

    const handleClearSearchClick = useCallback(() => {
        setSearchValue("");

        // Очищаем параметр search из URL
        const params = new URLSearchParams(searchParams);
        params.delete('search');

        // Сохраняем userId если он есть
        if (user?.id) {
            params.set('userId', user.id.toString());
        }

        setSearchParams(params);

        if (user?.id) {
            updPersonInfoId(user.id);
        }
    }, [user?.id, updPersonInfoId, searchParams, setSearchParams]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && searchValue.length >= 2) {
            handleSearchClick();
        }
    }, [searchValue, handleSearchClick]);

    // Восстанавливаем состояние из URL при монтировании
    useEffect(() => {
        const userIdFromUrl = searchParams.get('userId');
        const searchFromUrl = searchParams.get('search');

        // Если есть поисковый запрос в URL, выполняем поиск
        if (searchFromUrl && searchFromUrl.length >= 2) {
            postRequest({ search: searchFromUrl });
        }

        // Если есть userId в URL и он отличается от текущего, обновляем данные
        if (userIdFromUrl && user?.id && parseInt(userIdFromUrl) !== data.info.id) {
            updPersonInfoId(parseInt(userIdFromUrl));
        }
    }, []); // Выполняем только при монтировании

    useEffect(() => {
        if (data?.teams) {
            initDataTable(data.teams);
        }
    }, [data, initDataTable]);

    useEffect(() => {
        if (dataArrProp) {
            setData(dataArrProp);
        }
    }, [dataArrProp]);

    return (
        <>
            {alert && <Alerting get={alert} />}

            <Box>
                <Card>
                    <Box display='flex' alignItems='center' justifyContent='space-between'>
                        <Typography variant="h5">Структура</Typography>
                    </Box>
                    <CardContent>
                        <Grid container spacing={0}>
                            <Grid item xs={12} md={6} gap={2} display="flex" flexWrap="wrap" sx={{ mb: { xs: 6, lg: 0 } }}>
                                <InfoBox
                                    value={`${data.info.first_name} (${data.info.login})`}
                                    subtitle="Логин пользователя"
                                />
                                <InfoBox
                                    value={`${data.count} чел.`}
                                    subtitle="Всего людей"
                                />
                                <InfoBox
                                    value={data.info.phone}
                                    subtitle="Номер телефона"
                                />
                                <InfoBox
                                    value={dayjs(data.info.created_at).format("DD.MM.YYYY HH:mm")}
                                    subtitle="Дата регистрации"
                                />
                                <InfoBox
                                    value={data.info.email}
                                    subtitle="E-mail"
                                />
                                <Box sx={{ background: palette.lightWhiteBg, borderRadius: 1, p: 1 }}>
                                    <Typography variant="h5">
                                        {data.info.telegram ? (
                                            <a href={data.info.telegram} rel="noreferrer" target="_blank">
                                                Перейти
                                            </a>
                                        ) : (
                                            'Отсутствует'
                                        )}
                                    </Typography>
                                    <Typography sx={{ color: "#9c9c9c" }}>Телеграм</Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6} gap={2} display="flex" flexWrap="wrap">
                                <InfoBox
                                    value={`${data.referer?.name || 'Отсутствует'} ${data.referer?.login ? `(${data.referer.login})` : ''}`}
                                    subtitle="Куратор"
                                />
                                <InfoBox
                                    value={data.referer?.phone || "Отсутствует"}
                                    subtitle="Телефон куратора"
                                />
                                <InfoBox
                                    value={data.referer?.created_at ? dayjs(data.referer.created_at).format("DD.MM.YYYY HH:mm") : "неизвестно"}
                                    subtitle="Дата рег-ции куратора"
                                />
                                <InfoBox
                                    value={data.referer?.email || "Отсутствует"}
                                    subtitle="E-mail куратора"
                                />
                                <Box sx={{ background: palette.lightWhiteBg, borderRadius: 1, p: 1 }}>
                                    <Typography variant="h5">
                                        {data.referer?.telegram ? (
                                            <a href={data.referer.telegram} rel="noreferrer" target="_blank">
                                                Перейти
                                            </a>
                                        ) : (
                                            'Отсутствует'
                                        )}
                                    </Typography>
                                    <Typography sx={{ color: "#9c9c9c" }}>Телеграм куратора</Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {user?.id !== data.info.id && (
                            <Box mt={3}>
                                <Button
                                    size="large"
                                    sx={{ mr: 1 }}
                                    color="primary"
                                    variant="contained"
                                    onClick={() => handleUpdPersonInfoId(user?.id || 0)}
                                >
                                    В начало
                                </Button>
                                <Button
                                    size="large"
                                    variant="contained"
                                    color="success"
                                    onClick={() => handleUpdPersonInfoId(data.info.referer_id || 0)}
                                >
                                    Назад
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                <Box py={2}>
                    <Typography mb={1} variant="h6" fontWeight={600}>
                        Поиск партнёра
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="🔍  Поиск по логину"
                        value={searchValue}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#9c9c9c' }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleSearchClick}
                                        disabled={searchValue.length < 2 || isLoading}
                                        edge="end"
                                        color="primary"
                                        sx={{
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            '&:hover': { bgcolor: 'primary.dark' },
                                            '&:disabled': { bgcolor: '#e0e0e0' }
                                        }}
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {searchValue.length > 0 && searchValue.length < 2 && (
                        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                            Введите минимум 2 символа для поиска
                        </Typography>
                    )}

                    {searchValue.length > 0 && (
                        <Button
                            onClick={handleClearSearchClick}
                            size="small"
                            sx={{ mt: 2, '&:hover': { bgcolor: palette.blueHover }, mr: 0.5 }}
                        >
                            Очистить
                        </Button>
                    )}
                </Box>
            </Box>

            <Box pb={3} sx={{ width: "100%", overflow: "hidden" }}>
                <MaterialReactTable table={table} />
            </Box>
        </>
    );
};